import { useState } from 'react';
import { getFormUrl } from './formHelper';

export default function HeroSection() {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('phone_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormSubmitted(true);
      return;
    }
    setSubmitting(true);
    setFormError('');
    try {
      const formData = new FormData(form);
      const url = await getFormUrl();
      const resp = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const text = await resp.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(text); } catch { /* ignore */ }
      const code = (parsed as { code?: string })?.code;
      const msg = (parsed as { meta?: { message?: string } })?.meta?.message || (parsed as { message?: string })?.message || '';
      if (resp.ok && code === 'OK') {
        setFormSubmitted(true);
        form.reset();
      } else {
        const serverMsg = typeof msg === 'string' ? msg : '';
        setFormError(serverMsg || '提交失败，请稍后再试');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="hero" className="relative w-full min-h-[600px] md:min-h-[700px] flex items-center overflow-hidden">
      <img
        src="https://readdy.ai/api/search-image?query=Dark%20dramatic%20abstract%20composition%20with%20warm%20crimson%20and%20deep%20charcoal%20gradients%2C%20organic%20fluid%20shapes%2C%20theatrical%20spotlight%20effect%20on%20rich%20textured%20surface%2C%20cinematic%20atmosphere%2C%20mysterious%20vibrant%20red%20accents%20against%20dark%20backdrop%2C%20editorial%20quality%2C%20no%20text%2C%20no%20people&width=1800&height=900&seq=hero-bg-2026&orientation=landscape"
        alt="黑料大赛背景"
        className="absolute inset-0 w-full h-full object-cover object-top"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/35 to-black/55"></div>

      <div className="relative z-10 w-full px-4 md:px-6 py-20 md:py-24">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
          <div className="flex-1 w-full text-center lg:text-left">
            <span className="inline-block px-4 py-1.5 rounded-full bg-accent-500/20 text-accent-300 text-xs font-semibold tracking-wide mb-5 whitespace-nowrap">
              🔥 实时追踪 · 现场吃瓜
            </span>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-black text-background-50 leading-tight">
              黑料大赛
              <span className="block text-accent-400 text-xl md:text-3xl lg:text-4xl mt-2 font-bold">
                吃瓜现场直击
              </span>
            </h1>
            <p className="mt-4 md:mt-6 text-base md:text-lg text-background-300 leading-relaxed max-w-xl mx-auto lg:mx-0">
              网红撕逼、明星丑闻、娱乐圈猛料，一网打尽。作为全网最硬核的吃瓜现场，我们每日追踪最新八卦事件，汇集深度扒皮、独家爆料与瓜友辣评，让你不错过任何一个精彩瞬间。目前覆盖抖音、微博、小红书三大平台热点，每周更新 50+ 条实时快讯。
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-4 md:gap-6 justify-center lg:justify-start">
              <div className="flex items-center gap-2 text-background-200 text-sm">
                <span className="w-2 h-2 rounded-full bg-accent-500"></span>
                <span>每日更新</span>
              </div>
              <div className="flex items-center gap-2 text-background-200 text-sm">
                <span className="w-2 h-2 rounded-full bg-accent-500"></span>
                <span>覆盖 3 大平台</span>
              </div>
              <div className="flex items-center gap-2 text-background-200 text-sm">
                <span className="w-2 h-2 rounded-full bg-accent-500"></span>
                <span>周更 50+ 快讯</span>
              </div>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start">
              <a
                href="#carousel"
                className="px-8 py-3 bg-accent-500 text-background-950 font-bold rounded-lg hover:bg-accent-400 transition-colors duration-200 text-sm whitespace-nowrap cursor-pointer"
              >
                立即吃瓜
              </a>
              <a
                href="#topics"
                className="px-8 py-3 border border-background-300 text-background-100 font-semibold rounded-lg hover:bg-background-50/10 transition-colors duration-200 text-sm whitespace-nowrap cursor-pointer"
              >
                浏览话题
              </a>
            </div>

            <div className="mt-8">
              {formSubmitted ? (
                <p className="text-accent-400 text-sm font-medium">投稿已收到，感谢爆料！</p>
              ) : (
                <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row items-center gap-3 justify-center lg:justify-start" data-readdy-form="">
                  <input
                    type="email"
                    name="email"
                    placeholder="输入邮箱，订阅最新吃瓜快讯"
                    className="w-full sm:w-72 px-4 py-3 rounded-lg bg-background-50/10 border border-background-300/30 text-background-50 placeholder:text-background-400 text-sm focus:outline-none focus:border-accent-400 transition-colors"
                    required
                  />
                  <input
                    type="text"
                    name="phone_alt"
                    className="honeypot-field"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-6 py-3 bg-primary-500 text-background-50 font-semibold rounded-lg hover:bg-primary-400 transition-colors duration-200 text-sm whitespace-nowrap cursor-pointer disabled:opacity-50"
                  >
                    {submitting ? '提交中...' : '订阅'}
                  </button>
                </form>
              )}
              {formError && (
                <p className="mt-2 text-red-400 text-xs text-center lg:text-left">{formError}</p>
              )}
            </div>
          </div>

          <div className="flex-1 w-full max-w-lg lg:max-w-none">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://readdy.ai/api/search-image?query=Dramatic%20editorial%20composition%20with%20glowing%20smartphone%20screen%20displaying%20vibrant%20red%20social%20media%20feed%2C%20dark%20moody%20backdrop%20with%20theatrical%20crimson%20lighting%2C%20floating%20notification%20bubbles%2C%20cinematic%20atmosphere%2C%20modern%20tech%20aesthetic%2C%20high%20contrast%2C%20no%20text%2C%20no%20human%20faces&width=900&height=700&seq=hero-main-2026&orientation=portrait"
                alt="黑料大赛吃瓜现场主视觉"
                className="w-full h-auto object-cover"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-6">
                <p className="text-background-100 text-sm font-medium">今日热瓜：某顶流男星深夜发文引热议</p>
                <p className="text-accent-400 text-xs mt-1">2.3 万瓜友正在围观 →</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}